import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Panel } from "@/components/Panel";
import { Paginacion } from "@/components/Paginacion";
import { ClienteForm, validarCliente } from "@/components/ClienteForm";
import { useBusquedaLive } from "@/lib/busquedaHeader";
import { useAuth } from "@/lib/auth";
import { Pencil, X, Plus } from "lucide-react";

const TIPO_LABEL = { fisica: "Física", juridica: "Jurídica" };

export default function ClientesGestion() {
  const navigate = useNavigate();
  const { tienePermiso } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [q] = useBusquedaLive("Buscar en clientes…");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [cargando, setCargando] = useState(false);
  const [edit, setEdit] = useState(null);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get("/clientes", { params: { q: q.trim() || undefined, page, page_size: pageSize } });
      setClientes(r.data.items);
      setMeta({ total: r.data.total, total_pages: r.data.total_pages });
    } finally { setCargando(false); }
  }, [q, page, pageSize]);

  useEffect(() => { setPage(1); }, [q, pageSize]);
  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  const eliminar = async (c) => {
    const nombre = [c.nombre, c.primer_apellido].filter(Boolean).join(" ");
    if (!window.confirm(`¿Eliminar el cliente "${nombre}"?`)) return;
    try { await api.delete(`/clientes/${c.id}`); toast.success("Cliente eliminado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const guardarEdicion = async () => {
    const e = validarCliente(edit);
    setErrores(e);
    if (Object.keys(e).length) return;
    setGuardando(true);
    try {
      await api.patch(`/clientes/${edit.id}`, edit);
      toast.success("Cliente actualizado");
      setEdit(null); setErrores({});
      cargar();
    } catch (err) {
      const msg = errorMsg(err);
      if (err?.response?.status === 409) setErrores({ person_id: msg });
      toast.error(msg);
    } finally { setGuardando(false); }
  };

  return (
    <div data-testid="clientes-gestion-page" className="space-y-4">
      <Panel titulo="Gestión de Clientes" testId="clientes-gestion-panel">
        {tienePermiso("clientes.crear") && (
          <div className="flex justify-end mb-4">
            <Button data-testid="cliente-nuevo-btn" onClick={() => navigate("/clientes")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Plus size={16} className="mr-1" /> Nuevo cliente
            </Button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table data-testid="clientes-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 font-semibold">Nombre / Razón Social</th>
                <th className="py-2.5 pr-3 font-semibold">ID</th>
                <th className="py-2.5 pr-3 font-semibold">Teléfono</th>
                <th className="py-2.5 pr-3 font-semibold">Tipo</th>
                <th className="py-2.5 pr-3 font-semibold">Ubicación</th>
                <th className="py-2.5 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {clientes.map((c) => (
                <tr key={c.id} data-testid={`cliente-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2.5 pr-3 font-medium text-slate-800">
                    {[c.nombre, c.primer_apellido, c.segundo_apellido].filter(Boolean).join(" ")}
                  </td>
                  <td className="py-2.5 pr-3">{c.person_id}</td>
                  <td className="py-2.5 pr-3">{c.telefono}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      c.tipo === "fisica" ? "bg-teal-50 text-teal-700" : "bg-indigo-50 text-indigo-700"}`}>
                      {TIPO_LABEL[c.tipo] || c.tipo}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-slate-600">{c.ubicacion}</td>
                  <td className="py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      {tienePermiso("clientes.editar") && (
                      <button data-testid={`cliente-editar-btn-${c.id}`} title="Editar"
                        onClick={() => { setEdit({ ...c }); setErrores({}); }}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100">
                        <Pencil size={13} />
                      </button>
                      )}
                      {tienePermiso("clientes.borrar") && (
                      <button data-testid={`cliente-eliminar-btn-${c.id}`} title="Eliminar" onClick={() => eliminar(c)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                        <X size={14} />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {clientes.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-slate-400" data-testid="clientes-vacio">
                  No se encontraron clientes
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Paginacion page={page} totalPages={meta.total_pages} total={meta.total} pageSize={pageSize}
          onPage={setPage} onPageSize={setPageSize} cargando={cargando} />
      </Panel>

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) { setEdit(null); setErrores({}); } }}>
        <DialogContent data-testid="cliente-editar-dialog" className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Editar cliente</DialogTitle>
            <DialogDescription>Modifique los datos del cliente y guarde los cambios.</DialogDescription>
          </DialogHeader>
          {edit && (
            <>
              <ClienteForm valor={edit} onChange={setEdit} errores={errores} />
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" data-testid="cliente-editar-cancelar-btn"
                  className="border-teal-500 text-teal-600 hover:bg-teal-50 hover:text-teal-700"
                  onClick={() => { setEdit(null); setErrores({}); }}>Cancelar</Button>
                <Button data-testid="cliente-guardar-btn" onClick={guardarEdicion} disabled={guardando}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6">
                  {guardando ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
